import React, { useState, useEffect, ReactNode, Children, isValidElement, useRef, useMemo, startTransition } from 'react'
import { NextImage } from '@/components/NextImage'
import { useImage } from '@/hooks/images/useImage'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faStar } from "@fortawesome/free-solid-svg-icons"
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react'
import { NavigationTabs } from '../NavigationTabs'
import { SwiperOptions } from 'swiper/types'
import { useWindowDimensions } from '@/hooks/utils/useWindowDimensions'

import classNames from 'classnames'
import classes from './styles.module.scss'
import 'swiper/css'

interface ITeamTabsProps {
  children: ReactNode
  showTabs?: boolean
  preselectedTab?: string | number | null
}

interface ITabProps {
  label: string
  tabId: string | number // Ensure tabId is required
  isActive?: boolean
  onClick?: (tabId: string) => void
  onTabClick?: any
  favorite?: boolean
  tabStyle?: React.CSSProperties
  flag: string
}

interface IContentProps {
  tabId: string | number
  children: ReactNode
  tabContentStyle?: React.CSSProperties
  isActive?: boolean
}

const TeamTabs: React.FC<ITeamTabsProps> & {
  Tab: React.FC<ITabProps>
  Content: React.FC<IContentProps>
} = (props) => {
  const {
    children,
    showTabs = true,
    preselectedTab = null
  } = props
  const [activeTab, setActiveTab] = useState<any>(preselectedTab)
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null)
  const { isMobile, isTablet } = useWindowDimensions()

  const didInit = useRef(false)

  useEffect(() => {
    const allTabs = Children.toArray(children).filter((child) => {
      return isValidElement<ITabProps>(child) && child.type === TeamTabs.Tab
    }) as React.ReactElement<ITabProps>[]

    if (allTabs.length === 0) {
      if (activeTab !== null) startTransition(() => setActiveTab(null))
      return
    }

    const tabIds = allTabs.map((tab) => tab.props.tabId.toString())

    if (!didInit.current) {
      // Only execute this logic once when mounting
      didInit.current = true

      if (preselectedTab) {
        startTransition(() => setActiveTab(preselectedTab))
        return
      }

      if (!activeTab || !tabIds.includes(activeTab)) {
        startTransition(() => setActiveTab(tabIds[0]))
      }

      return
    }

    // If the current tab was deleted, the next closest tab is selected
    if (!tabIds.includes(activeTab)) {
      startTransition(() => setActiveTab(tabIds[0]))
    }
  }, [children, activeTab, preselectedTab])


  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId)
  }

  useEffect(() => {
    return () => {
      if (swiperInstance) {
        swiperInstance.destroy(true, true)
      }
    }
  }, [swiperInstance])

  //Force update when tab array changes its length
  useEffect(() => {
    if (swiperInstance) {
      setTimeout(() => {
        swiperInstance.update()
      }, 0)
    }
  }, [children, swiperInstance])

  const getSwiperConfig: () => SwiperOptions = () => ({
    allowTouchMove: true,
    autoplay: false,
    slidesPerView: 5,
    spaceBetween: 8,
    breakpoints: {
      300: {
        slidesPerView: 2,
      },
      640: {
        slidesPerView: 2,
      },
      768: {
        slidesPerView: 3,
      },
      1024: {
        slidesPerView: 5,
      },
    }
  })

  const SwiperConfig = getSwiperConfig()

  return (
    <div className={classes.teamTabs}>
      {showTabs &&
        <div
          className={classNames({
            [classes.tabs]: true,
            [classes.fullWidth]: (isMobile || isTablet) || (!swiperInstance?.allowSlidePrev && !swiperInstance?.allowSlideNext)
          })}
        >
          <Swiper
            navigation={false}
            onSwiper={(swiper) => setSwiperInstance(swiper)}
            {...SwiperConfig}
          >
            {Children.map(children, (child) => {
              if (isValidElement<ITabProps>(child) && child.type === TeamTabs.Tab) {
                return (
                  <SwiperSlide>
                    {React.cloneElement(child, {
                      isActive: child.props.tabId === activeTab,
                      onClick: handleTabClick,
                    })}
                  </SwiperSlide>
                )
              }
              return null
            })}
          </Swiper>
          {swiperInstance && (swiperInstance?.allowSlidePrev || swiperInstance?.allowSlideNext) && (!isMobile && !isTablet) &&
            <NavigationTabs
              swiper={swiperInstance}
            />
          }
        </div>
      }
      <div className={classes.content}>
        {Children.map(children, (child) => {
          if (isValidElement<IContentProps>(child) && child.type === TeamTabs.Content) {
            const isActive = child.props.tabId === activeTab
            return React.cloneElement(child, { isActive })
          }
          return null
        })}
      </div>
    </div >
  )
}

TeamTabs.Tab = Object.assign(({
  label,
  tabId,
  isActive,
  onClick,
  tabStyle,
  flag,
  onTabClick,
  favorite
}: ITabProps) => {
  const { getImageUrl } = useImage({
    cut: 'square',
    scale: '50'
  })

  const handleClick = () => {
    onClick?.(tabId.toString()), onTabClick()
  }

  return (
    <div
      className={`${classes.tab} ${isActive ? classes.active : ''}`}
      onClick={handleClick}
      style={{ ...tabStyle }}
    >
      <NextImage
        src={getImageUrl({ id: flag }) || ''}
        width={32}
        height={32}
        alt={label ?? ''}
      />
      <span>{label}</span>
      {favorite && (
        <FontAwesomeIcon
          icon={faStar}
          className={classes.favorite}
        />
      )}
    </div>
  )
}, { displayName: 'TeamTabs.Tab' })

TeamTabs.Content = Object.assign(({ children, isActive, tabContentStyle }: IContentProps & { isActive?: boolean }) => {
  return (
    <div
      className={`${classes.tabContent} ${isActive ? classes.activeContent : ''}`}
      style={{ ...tabContentStyle }}
    >
      {children}
    </div>
  )
}, { displayName: 'TeamTabs.Content' })


export { TeamTabs }